import React, { useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { en } from '../translations/en';
import { validatePassword } from '../utils/passwordValidation';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';
import { usePasswordVisibility } from '../hooks/usePasswordVisibility';

export const ChangePassword: React.FC = () => {
  const theme = useTheme();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { showPassword: showNewPassword, inputType: newPasswordInputType, toggleVisibility: toggleNewPasswordVisibility } = usePasswordVisibility();
  const { showPassword: showConfirmPassword, inputType: confirmPasswordInputType, toggleVisibility: toggleConfirmPasswordVisibility } = usePasswordVisibility();
  const [error, setError] = useState<string | null>(null);
  const { changePassword, isLoading } = useAuth();
  const navigate = useNavigate();

  // Get challenge data from sessionStorage and window object
  const storedChallenge = sessionStorage.getItem('passwordChangeChallenge');
  const challengeInfo = storedChallenge ? JSON.parse(storedChallenge) : {};
  const cognitoUser = (window as { cognitoUserChallenge?: unknown }).cognitoUserChallenge;

  const { username = challengeInfo.username, userAttributes = challengeInfo.userAttributes, requiredAttributes = challengeInfo.requiredAttributes } = challengeInfo;
  const challengeData = { cognitoUser, userAttributes, requiredAttributes };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(en.changePassword.errors.passwordMismatch);
      return;
    }

    // Validate password complexity
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join('. '));
      return;
    }

    try {
      await changePassword(username, newPassword, challengeData);
      // Clean up stored challenge data
      sessionStorage.removeItem('passwordChangeChallenge');
      delete (window as { cognitoUserChallenge?: unknown }).cognitoUserChallenge;
      navigate('/');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : en.changePassword.errors.changeFailed;
      setError(errorMessage);
    }
  };

  if (!username || !cognitoUser) {
    navigate('/auth');
    return null;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2
    }}>
      <Card sx={{
        maxWidth: 400,
        width: '100%',
        boxShadow: theme.shadows[4]
      }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ color: theme.palette.primary.main }}
          >
            {en.changePassword.title}
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {en.changePassword.subtitle}
          </Typography>

          <Box component="form" onSubmit={handleChangePassword}>
            <TextField
              fullWidth
              label={en.changePassword.newPassword}
              type={newPasswordInputType}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label="toggle new password visibility"
                      onClick={toggleNewPasswordVisibility}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordStrengthIndicator password={newPassword} compact={true} />

            <TextField
              fullWidth
              label={en.changePassword.confirmPassword}
              type={confirmPasswordInputType}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label="toggle confirm password visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark
                }
              }}
            >
              {isLoading ? en.changePassword.changing : en.changePassword.changeButton}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
